/**
 * Created by Alex Poh. on 26/04/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import * as moment from 'moment';
import { of, switchMap, takeUntil, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { IPerformanceChart, IPerformanceChartSubject } from './student-performance-chart.interface';
import { StudentPerformanceChartService } from './student-performance-chart.service';

@Component({
  selector: 'curr-student-performance-chart',
  templateUrl: './student-performance-chart.component.html',
  styleUrls: ['./student-performance-chart.component.scss'],
})
export class StudentPerformanceChartComponent implements OnInit, OnDestroy {

  @Input() studentId;

  chartAverageBenchmark: string;

  performanceChartSubjects = [];
  performanceResultsMark = '';
  performanceResultDate = '';
  performanceResultsChart = {
    grades: [],
    averageBenchmark: [],
  };
  performanceResultsChartMonth = [];
  performanceChartLoading = true;
  performanceChartNoData = false;

  unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private studentPerformanceChartService: StudentPerformanceChartService,
    private translateService: TranslateService
  ) {

  }

  ngOnInit(): void {
    this.getPerformanceChartData();

    // Subscribe to language changes to update month labels when language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.performanceResultsChart.grades.length > 0) {
          // Re-process chart data with new translations
          this.performanceResultsChartMonth = [];
          const lineChartLabels = this.getTranslatedMonthLabels();
          this.performanceResultsChart.grades.forEach((_, index) => {
            const item = this.performanceResultsChart.grades[index];
            const monthIndex = parseInt(this.performanceResultsChartMonth[index]?.split('-')[1] || '1', 10) - 1;
            this.performanceResultsChartMonth[index] = lineChartLabels[monthIndex];
          });
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  performanceChartSubjectsChange(value: IPerformanceChartSubject) {
    this.getPerformanceChartWithoutSubjects(value.subjectName, value.studyYearId, value.courseId, value.schoolYearSettingsId);
  }

  private getPerformanceChartData(): void {
    this.studentPerformanceChartService
      .getChartSubjects(this.studentId)
      .pipe(
        switchMap((subjectsOptions) => {
          if (subjectsOptions.subjectToCourse.length > 0) {
            const sortedList = subjectsOptions.subjectToCourse.sort((a, b) => a.subjectName.localeCompare(b.subjectName));

            sortedList.forEach((item) => {
              item.studyYearId = subjectsOptions.levelToCourse.find(levelItem => levelItem.courses == item.courseId)?.levels;
              const courseValue = subjectsOptions.courses.find(courseItem => courseItem.id == item.courseId);
              item.schoolYearSettingsId = courseValue.schoolYearSettingsId;
              this.performanceChartSubjects.push({ value: item, label: item.subjectName + ' - Year - ' + courseValue.value });
            });

            return this.studentPerformanceChartService.getChartData(
              this.performanceChartSubjects[0].value.subjectName,
              this.performanceChartSubjects[0].value.studyYearId,
              this.performanceChartSubjects[0].value.courseId,
              this.studentId,
              this.performanceChartSubjects[0].value.schoolYearSettingsId
            );
          }
          this.performanceChartNoData = true;
          return of();
        }),
        takeUntil(this.unsubscribe),
      )
      .subscribe(
        (chartData) => {
          if (chartData) this.processPerformanceChart(chartData);
        },
        (error) => {
          this.performanceChartLoading = false;
        },
      );
  }

  private getPerformanceChartWithoutSubjects(subject: string, studyYearId: number, courseId: number, schoolYearSettingsId: number): void {
    this.studentPerformanceChartService
      .getChartData(subject, studyYearId, courseId, this.studentId, schoolYearSettingsId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (chartData) => {
          if (chartData) this.processPerformanceChart(chartData);
        },
        (error) => {
          this.performanceChartLoading = false;
        },
      );
  }

  private getTranslatedMonthLabels(): string[] {
    return [
      this.translateService.instant('shared.months.jan'),
      this.translateService.instant('shared.months.feb'),
      this.translateService.instant('shared.months.mar'),
      this.translateService.instant('shared.months.apr'),
      this.translateService.instant('shared.months.may'),
      this.translateService.instant('shared.months.jun'),
      this.translateService.instant('shared.months.jul'),
      this.translateService.instant('shared.months.aug'),
      this.translateService.instant('shared.months.sep'),
      this.translateService.instant('shared.months.oct'),
      this.translateService.instant('shared.months.nov'),
      this.translateService.instant('shared.months.dec')
    ];
  }

  private processPerformanceChart(chartData: IPerformanceChart) {
    this.performanceResultsMark = chartData.averageGrade;

    this.performanceResultDate = `${moment(chartData.startDate).format('D MMMM YYYY')} - ${moment(chartData.endDate).format('D MMMM YYYY')}`;
    const chartResults = {
      grades: [],
      averageBenchmark: [],
    };

    this.chartAverageBenchmark = chartData.averageBenchmark;

    const month = [];
    const lineChartLabels = this.getTranslatedMonthLabels();
    chartData.grades.forEach((item) => {
      chartResults.averageBenchmark.push(chartData.averageBenchmark);
      if (item.gradeLabel === '-') {
        chartResults.grades.push(null)
      } else {
        chartResults.grades.push(item.gradeLabel)
      };
      month.push(lineChartLabels[item.month - 1]);
    });

    if (chartResults.grades.filter(item => !!item).length === 0) chartResults.averageBenchmark = [];

    this.performanceResultsChart = chartResults;
    this.performanceResultsChartMonth = month;
    this.performanceChartLoading = false;
  }

}
